import axios, { type AxiosError } from 'axios';

/**
 * Cross-tab logout signal key. We do NOT store the session token in
 * localStorage anymore — the session lives in an httpOnly cookie. This key
 * carries only a timestamp written on logout so other tabs can react.
 */
const LOGOUT_SIGNAL_KEY = 'castvo_logged_out';

let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(handler: () => void) {
  unauthorizedHandler = handler;
}

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
  // Session lives in `castvo_session` httpOnly cookie. The browser only
  // attaches cross-origin cookies when this flag is set.
  withCredentials: true,
});

apiClient.interceptors.response.use(
  (r) => r,
  (error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>) => {
    // Lift server-side `message` into error.message for easy catch-side display (spec §6.3)
    const data = error.response?.data;
    if (data?.message) {
      error.message = data.message;
    }
    // 401 → notify auth store via callback; don't hard-redirect (spec §9.4)
    if (error.response?.status === 401) {
      unauthorizedHandler?.();
    }
    return Promise.reject(error);
  },
);

// Cross-tab logout sync — when one tab calls logout(), it writes a timestamp
// to LOGOUT_SIGNAL_KEY which fires `storage` in other tabs.
window.addEventListener('storage', (e) => {
  if (e.key === LOGOUT_SIGNAL_KEY && e.newValue) {
    unauthorizedHandler?.();
  }
});

export { LOGOUT_SIGNAL_KEY };
